<template>
  <div class='service-provider-worker-profile-form'>
    <v-form ref="form" v-if='serviceProviderCompanyProfileDetails'>
      <v-text-field
        v-model="serviceProviderCompanyProfileDetails.experience"
        :label="$t('sp.registration.company.services.years.of.experience')"
        :placeholder="$t('sp.registration.company.services.years.of.experience.placeholder')"
        dense
        outlined
        required
      />
      <div class="company-services-sub-title">
        {{ $t('sp.registration.company.services.select.services') }}
      </div>
      <v-list
        flat
        class="services-list"
      >
        <v-list-group
          v-for="service in topLevelServicesWithSubs"
          :key="service"
          :append-icon="''"
        >
          <template v-slot:activator>
            <v-list-item-icon>
              <v-icon color="#E7E9EC" small>add</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="subService in formattedServicesOptions[service]"
            :key="subService"
          >
            <template>
              <v-list-item-action>
                <custom-checkbox
                  color="cold-marine"
                  size="s"
                  :value="flatServicesOptionsObject[subService]"
                  @input="onCheck(subService)"
                ></custom-checkbox>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title v-text="$t(`${subService}`)"></v-list-item-title>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list-group>
        <v-list-item
          v-for="service in topLevelServicesWithoutSubs"
          :key="service"
        >
          <template>
            <v-list-item-action>
              <custom-checkbox
                :value="flatServicesOptionsObject[service]"
                color="cold-marine"
                @input="onCheck(service)"
                size="s"
              ></custom-checkbox>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-list-item>
      </v-list>
      <div class="add-ip-form">
        <div class='ip-rows'>
          <div v-for="(value, index) in serviceProviderCompanyProfileDetails.whitelistIP" :key="index">
            <div class="text-field-section">
              <text-field
                :rules="[rules.hostWithRange]"
                type="text"
                v-model="serviceProviderCompanyProfileDetails.whitelistIP[index]"
                class="login"
                :label="$t('registration.sp.freelancer.company.certification.white.ip.label')"
                :placeholder="$t('registration.sp.freelancer.company.certification.white.ip.placeholder')"
              />
              <span
                v-if='isDeletable(index)'
                class="delete-icon material-icons"
                @click="removeIp(index)"
              >
            highlight_off
          </span>
            </div>
          </div>
        </div>
        <div class="add-new-ip-section">
          <v-btn
            color='#B331CF'
            class="add-btn" @click="addIp">
            <span class="plus">+</span>{{ $t('registration.sp.freelancer.company.certification.white.ip.add') }}
          </v-btn>
        </div>
      </div>
      <text-field
        type="text"
        v-model="serviceProviderCompanyProfileDetails.linkedinLink"
        :rules='[rules.linkedin]'
        class="linkedin-link"
        :label="$t('registration.sp.company.certification.linkedin.label')"
        :placeholder="$t('registration.sp.freelancer.company.certification.linkedin.placeholder')"
      />
      <v-autocomplete
        :items="regions"
        :label="$t('registration.workspace.info.office.location')"
        :placeholder="$t('registration.workspace.info.office.location.placeholder')"
        v-model="serviceProviderCompanyProfileDetails.region"
        item-text="text"
        item-value='name'
        dense
        outlined
        return-object
      />
      <v-autocomplete
        :items="amountOfEmployees"
        :label="$t('registration.workspace.info.employees')"
        :placeholder="$t('registration.workspace.info.employees.placeholder')"
        item-value="amount"
        v-model="serviceProviderCompanyProfileDetails.amountOfEmployees"
        dense
        outlined
        return-object
      />
    </v-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { rules } from '@/utils/validationUtils';
import TextField from '@/components/common/TextField.vue';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';

export default {
  name: 'EditServiceProviderCompanyProfileForm',
  components: {
    TextField,
    CustomCheckbox,
  },
  created() {
    this.serviceProviderCompanyProfileDetails.region.value = this.$t(this.serviceProviderCompanyProfileDetails.region.name);
  },
  computed: {
    ...mapGetters([
      'spCompanyCertificates',
      'serviceProviderCompanyProfileDetails',
      'serviceProviderCompanyFullRegionsOptions',
      'spCompanyAmountOfEmployees',
      'serviceProviderCompanyFullServicesOptions',
      'serviceProviderCompanyServices',
    ]),
    topLevelServicesWithoutSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => !this.formattedServicesOptions[service].length);
    },
    topLevelServicesWithSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => this.formattedServicesOptions[service].length);
    },
    formattedServicesOptions() {
      const servicesMap = new Map();
      this.serviceProviderCompanyFullServicesOptions.forEach(service => {
        if (service.shortName.includes('.sub.')) {
          const topLevelService = service.shortName.split('.sub.')[0];
          if (!servicesMap.has(topLevelService)) {
            servicesMap.set(topLevelService, [service.shortName]);
          } else {
            const currentSubServices = servicesMap.get(topLevelService);
            currentSubServices.push(service.shortName);
            servicesMap.set(topLevelService, currentSubServices);
          }
        } else if (!servicesMap.has(service.shortName)) {
          servicesMap.set(service.shortName, []);
        }
      });
      return this.mapToObj(servicesMap);
    },
    flatServicesOptionsObject() {
      const flatServicesObject = {};
      this.topLevelServicesWithoutSubs.forEach(
        service => {
          flatServicesObject[service] = this.serviceProviderCompanyServices.includes(service);
        },
      );
      this.topLevelServicesWithSubs.forEach(
        service => {
          this.formattedServicesOptions[service].forEach(
            subService => {
              flatServicesObject[subService] = this.serviceProviderCompanyServices.includes(subService);
            },
          );
        },
      );
      return flatServicesObject;
    },
    checkBoxValues() {
      const certificatesValueObject = {};
      this.spCompanyCertificates.forEach(certificate => {
        certificatesValueObject[certificate.code] = this.certificatesListChosen.includes(certificate.code);
      });
      return certificatesValueObject;
    },
    rules() {
      return {
        ...rules,
        // eslint-disable-next-line no-restricted-globals
        host: v => rules.host(v) || this.$t('registration.client.info.host.is.not.valid'),
        required: v => rules.required(v) || this.$t('required'),
      };
    },
    amountOfEmployees() {
      return this.spCompanyAmountOfEmployees.map(amount => ({
        text: this.$pt('registration.workspace.info.amount.of.employees', amount),
        amount,
      }));
    },
    regions() {
      return this.serviceProviderCompanyFullRegionsOptions.map(region => ({
        text: this.$t(region.name),
        ...region,
      }));
    },
  },
  methods: {
    isDeletable(index) {
      return index !== 0;
    },
    onCheck(service) {
      let chosenFullService = {};
      this.serviceProviderCompanyFullServicesOptions.forEach(serviceOption => {
        if (serviceOption.shortName === service) {
          chosenFullService = serviceOption;
        }
      });
      let isInServices = false;
      let indexOfService = -1;
      for (let index = 0; index < this.serviceProviderCompanyProfileDetails.services.length; index += 1) {
        const serviceItem = this.serviceProviderCompanyProfileDetails.services[index];
        if (serviceItem.shortName === chosenFullService.shortName) {
          isInServices = true;
          indexOfService = index;
          break;
        }
      }
      if (!isInServices) {
        this.serviceProviderCompanyProfileDetails.services.push(chosenFullService);
      } else {
        this.serviceProviderCompanyProfileDetails.services.splice(indexOfService, 1);
      }
    },
    mapToObj(map) {
      const obj = {};
      map.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    },
    addIp() {
      this.serviceProviderCompanyProfileDetails.whitelistIP.push('');
    },
    removeIp(index) {
      this.serviceProviderCompanyProfileDetails.whitelistIP.splice(index, 1);
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';
.v-input__icon--append {
  margin-top: 0 !important;
}

.add-ip-form {
  margin-bottom: rem(32);
  align-items: center;
  display: flex;
  flex-direction: column;
  .ip-rows {
    .text-field-section{
      display: flex;
      align-items: center;
      .v-text-field {
        max-width: rem(410);
        min-width: rem(410);
      }
      .delete-icon {
        color: var(--status-error);
        cursor: pointer;
        font-size: rem(16);
        margin-left: rem(5);
        margin-bottom: rem(5);
        margin-top: rem(17);
      }
    }
  }
  .add-new-ip-section {
    margin-top: rem(10);
    margin-bottom: rem(4);
    height: rem(16);
    color: var(--text-tertiary);
    font-family: $font-family-regular;
    font-size: rem(12);
    font-weight: 500;
    letter-spacing: rem(1.07);
    line-height: rem(16);
    text-align: center;
    .plus {
      font-size: rem(16);
    }
    .add-btn {
      width: rem(86);
      color: var(--text-on-brand);
    }
  }
}
</style>
